import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { SortingState } from '@tanstack/react-table'
import { History, Inbox } from 'lucide-react'
import { PageHeader } from '@/components/layout/page-header'
import { DataTable } from '@/components/custom/datatable'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Typography } from '@/components/ui/typography'
import { createColumn } from '@/lib/datatable/create-column'
import { cn } from '@/lib/utils'
import { IntakePanel } from '@/components/custom/deal-review/intake-panel'
import { DealCloudSendStatus } from '@/components/custom/deal-review/dealcloud-send-status'
import { DecisionSelect } from '@/components/custom/deal-review/deal-row-controls'
import { ratio } from '@/features/deal-review/deal-lead-fields'
import {
  DEAL_LEAD_VIEW_LABELS,
  DEAL_LEAD_VIEWS,
  EMPTY_LEAD_FILTERS,
  displayNameFor,
  isInProgress,
  filtersForView,
  hasActiveLeadFilters,
  sortsForView,
  statusFor,
  keepInPlace,
  leftWhileSending,
  type DealLeadFilterState,
  type HeldLead,
  type DealLeadView,
} from '@/features/deal-review/deal-lead-views'
import { MUTED_TEXT } from '@/features/deal-review/review-tones'
import { DealLeadDetailSheet } from '@/components/custom/deal-review/deal-lead-detail-sheet'
import { SelectionBar } from '@/components/custom/deal-review/selection-bar'
import type { LeadSelection } from '@/components/custom/deal-review/selection-bar'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useUndoableSend } from '@/hooks/use-deal-send-batches'
import {
  useCachedDealLeads,
  useDealLeadCounts,
  useDealLeadsSearch,
} from '@/hooks/use-deal-leads'
import type { ColumnDef } from '@/components/custom/datatable/types'
import { formatMillions } from '@/features/deal-review/deal-lead-fields'
import type { DealLeadDetail, DealLeadListItem } from '@/types/api'
import {
  OwnClick,
  TruncatedCell,
  SubSectorCell,
  RowProgress,
  MoneyCell,
  MarginCell,
  HeadquartersCell,
  SourceCell,
  ReceivedCell,
  RecommendationHint,
  StatusCell,
  CompanyCell,
  DealCloudStateBadge,
  NotSentMark,
  SelectAllHeader,
  SelectRowCell,
} from '@/components/custom/deal-review/inbox-cells'
import {
  HistoryActionsCell,
  InboxActionsCell,
  RowActionRail,
} from '@/components/custom/deal-review/inbox-action-cells'
import { NotesCell } from '@/components/custom/deal-review/inbox-notes-cell'
import {
  DecisionFilter,
  InboxToolbar,
} from '@/components/custom/deal-review/inbox-toolbar'

function CountTab({
  view,
  count,
  isLoading,
}: {
  view: DealLeadView
  count: number | undefined
  isLoading: boolean
}) {
  const Icon = VIEW_ICONS[view]

  return (
    <TabsTrigger value={view} className="gap-2">
      <Icon aria-hidden="true" className="size-4" />
      {DEAL_LEAD_VIEW_LABELS[view]}
      {isLoading ? (
        <Skeleton className="h-4 w-6" />
      ) : (
        <Typography as="span" variant="body-sm" className={MUTED_TEXT}>
          {count ?? 0}
        </Typography>
      )}
    </TabsTrigger>
  )
}

const VIEW_ICONS: Record<DealLeadView, typeof Inbox> = {
  inbox: Inbox,
  history: History,
}

// Scoped here: the shared DataTable's grid styling suits other tables with committed baselines.
const INBOX_TABLE_CLASSES = [
  'flex-1',
  // rounded-md: the shared table clips at md; a larger radius left a notch at the corners.
  'rounded-md border border-primary/10',
  '[&_th]:border-r-0 [&_td]:border-r-0',
  '[&_th:first-child]:border-l-0 [&_td:first-child]:border-l-0',
  '[&_th]:border-b-primary/10 [&_td]:border-b-primary/10',
  // Opaque: the header is sticky and a translucent band let rows show through.
  '[&_thead]:bg-background',
  '[&_thead_tr]:bg-primary/[0.03] [&_thead_tr:hover]:bg-primary/[0.03]',
  // Buttons too: the UA stylesheet resets text-transform on form controls (sortable headers).
  '[&_th]:text-[10.5px] [&_th]:font-bold [&_th]:uppercase [&_th]:tracking-[0.04em]',
  '[&_th_button]:uppercase',
  '[&_td]:whitespace-normal [&_td]:align-top [&_td]:py-3',
  '[&_th]:align-middle',
  '[&_th]:px-2 [&_td]:px-2',
  // Scoped: the company and contact grids share the checkbox primitive.
  '[&_[role=checkbox]]:size-3.75 [&_[role=checkbox]]:rounded-[4px]',
  '[&_[role=checkbox]]:border [&_[role=checkbox]]:border-primary/55',
  // Sum of fixed widths plus Description's floor; update it when a column changes.
  '[&_table]:min-w-[2110px]',
  '[&_thead]:sticky [&_thead]:top-0 [&_thead]:z-20',
  // Drops the scroller's pr-4 md:pr-8; `DndContext` renders no DOM, so the nesting is stable.
  '[&>div>div]:!pr-0',
].join(' ')

// Forced: the table writes pixel widths inline (GS-143). Positional: only Inbox has checkboxes.
const FLEX_COLUMN_CLASSES: Record<DealLeadView, string> = {
  inbox: [
    '[&_th:nth-child(4)]:!w-auto [&_td:nth-child(4)]:!w-auto [&_th:nth-child(4)]:!min-w-100 [&_td:nth-child(4)]:!min-w-100',
    // `DataTable` floors columns at 60px, so the tick column's width is overridden here.
    '[&_th:first-child]:!w-9 [&_td:first-child]:!w-9',
    '[&_th:first-child]:!min-w-9 [&_td:first-child]:!min-w-9',
  ].join(' '),
  history:
    '[&_th:nth-child(3)]:!w-auto [&_td:nth-child(3)]:!w-auto [&_th:nth-child(3)]:!min-w-100 [&_td:nth-child(3)]:!min-w-100',
}

function inboxTableClasses(view: DealLeadView) {
  return `${INBOX_TABLE_CLASSES} ${FLEX_COLUMN_CLASSES[view]}`
}

// From context: rebuilding columns makes new cell component types, remounting every cell.
interface InboxTableState {
  selection: LeadSelection
  leads: DealLeadListItem[]
  onSelect: (leadId: string) => void
  onDecided: (detail: DealLeadDetail) => void
  onSent: (detail: DealLeadDetail) => void
}

const InboxTableContext = createContext<InboxTableState | null>(null)

function useInboxTable(): InboxTableState {
  const state = useContext(InboxTableContext)
  if (!state) throw new Error('Inbox cells render inside the inbox table')
  return state
}

function InboxSelectAll() {
  const { selection, leads } = useInboxTable()
  return <SelectAllHeader selection={selection} leads={leads} />
}

function InboxSelectRow({ lead }: { lead: DealLeadListItem }) {
  const { selection } = useInboxTable()
  return (
    <OwnClick>
      <SelectRowCell lead={lead} selection={selection} />
    </OwnClick>
  )
}

function InboxCompany({ lead }: { lead: DealLeadListItem }) {
  const { onSelect } = useInboxTable()
  return <CompanyCell lead={lead} onSelect={onSelect} />
}

function InboxDecision({ lead }: { lead: DealLeadListItem }) {
  const { onDecided } = useInboxTable()
  return <DecisionSelect lead={lead} onDecided={onDecided} />
}

function InboxActions({
  lead,
  view,
}: {
  lead: DealLeadListItem
  view: DealLeadView
}) {
  const { onSent } = useInboxTable()
  return (
    <RowActionRail>
      {view === 'history' ? (
        <HistoryActionsCell lead={lead} onSent={onSent} />
      ) : (
        <InboxActionsCell lead={lead} onSent={onSent} />
      )}
    </RowActionRail>
  )
}

// Hand-built: `createColumn` types its header as a string.
function selectColumn(): ColumnDef<DealLeadListItem> {
  const meta = {
    fieldName: 'select',
    displayName: '',
    dataType: 'string',
    isFilterable: false,
    isSortable: false,
    isEditable: false,
  } as const

  return {
    ...meta,
    id: 'select',
    accessorFn: () => '',
    header: () => <InboxSelectAll />,
    width: 60,
    displayOrder: 0,
    isVisible: true,
    meta,
    cell: (info) => <InboxSelectRow lead={info.row.original} />,
  }
}

function useDealLeadColumns(view: DealLeadView): ColumnDef<DealLeadListItem>[] {
  return useMemo(
    () => [
      ...(view === 'history' ? [] : [selectColumn()]),
      createColumn<DealLeadListItem>({
        id: 'receivedAt',
        header: 'Received',
        accessorFn: (row) => row.receivedAt,
        isSortable: true,
        width: 108,
        cell: (info) => <ReceivedCell lead={info.row.original} />,
      }),
      createColumn<DealLeadListItem>({
        id: 'companyName',
        header: 'Company or project',
        accessorFn: (row) => displayNameFor(row) ?? '',
        width: 190,
        cell: (info) => <InboxCompany lead={info.row.original} />,
      }),
      createColumn<DealLeadListItem>({
        id: 'description',
        header: 'Description',
        accessorFn: (row) => row.description ?? '',
        width: 200,
        cell: (info) => (
          <TruncatedCell value={info.row.original.description} lines={3} />
        ),
      }),
      createColumn<DealLeadListItem>({
        id: 'subSector',
        header: 'Sub-sector',
        accessorFn: (row) => row.subSector ?? '',
        width: 160,
        cell: (info) => <SubSectorCell lead={info.row.original} />,
      }),
      createColumn<DealLeadListItem>({
        id: 'revenue',
        header: 'Revenue',
        accessorFn: (row) => formatMillions(row.revenue),
        isSortable: true,
        width: 96,
        cell: (info) => <MoneyCell value={info.row.original.revenue} />,
      }),
      createColumn<DealLeadListItem>({
        id: 'ebitda',
        header: 'EBITDA',
        accessorFn: (row) => formatMillions(row.ebitda),
        isSortable: true,
        width: 88,
        cell: (info) => <MoneyCell value={info.row.original.ebitda} />,
      }),
      createColumn<DealLeadListItem>({
        id: 'ebitdaMargin',
        header: 'EBITDA margin',
        accessorFn: (row) => ratio(row.ebitdaMarginPct) ?? '',
        isSortable: true,
        width: 148,
        cell: (info) => <MarginCell lead={info.row.original} />,
      }),
      ...(view === 'history'
        ? [
            createColumn<DealLeadListItem>({
              id: 'status',
              header: 'Status',
              accessorFn: (row) => statusFor(row).label,
              width: 176,
              cell: (info) => (
                <div className="flex items-center gap-1">
                  <StatusCell lead={info.row.original} />
                  <RecommendationHint lead={info.row.original} view={view} />
                </div>
              ),
            }),
          ]
        : [
            createColumn<DealLeadListItem>({
              id: 'disposition',
              header: 'Decision',
              accessorFn: (row) => row.disposition,
              width: 176,
              cell: (info) => (
                <div className="flex flex-col items-start gap-1">
                  <div className="flex items-center gap-0.5">
                    <OwnClick className="min-w-0">
                      <InboxDecision lead={info.row.original} />
                    </OwnClick>
                    <RecommendationHint lead={info.row.original} view={view} />
                    <NotSentMark lead={info.row.original} />
                  </div>
                  <DealCloudStateBadge lead={info.row.original} />
                </div>
              ),
            }),
          ]),
      createColumn<DealLeadListItem>({
        id: 'actions',
        header: 'Actions',
        accessorFn: () => '',
        width: 112,
        cell: (info) => <InboxActions lead={info.row.original} view={view} />,
      }),
      createColumn<DealLeadListItem>({
        id: 'notes',
        header: 'Notes',
        accessorFn: (row) => row.dispositionCommentary ?? '',
        width: 300,
        cell: (info) => (
          <NotesCell lead={info.row.original} readOnly={view === 'history'} />
        ),
      }),
      createColumn<DealLeadListItem>({
        id: 'geography',
        header: 'HQ',
        accessorFn: (row) => row.geography ?? '',
        // 176 and not less: GS-141.
        width: 176,
        cell: (info) => <HeadquartersCell lead={info.row.original} />,
      }),
      createColumn<DealLeadListItem>({
        id: 'source',
        header: 'Source',
        accessorFn: (row) => row.brokerCompany ?? row.senderName ?? '',
        width: 176,
        cell: (info) => <SourceCell lead={info.row.original} />,
      }),
    ],
    [view]
  )
}

function emptyStateFor(
  view: DealLeadView,
  filters: DealLeadFilterState,
  onClear: () => void
): {
  emptyTitle: string
  emptyDescription: string
  emptyActionLabel?: string
  onEmptyAction?: () => void
} {
  if (hasActiveLeadFilters(filters)) {
    return {
      emptyTitle: 'No deals match these filters.',
      emptyDescription: 'Widen them, or clear them to see everything again.',
      emptyActionLabel: 'Clear filters',
      onEmptyAction: onClear,
    }
  }

  return view === 'inbox'
    ? {
        emptyTitle: 'The inbox is empty.',
        emptyDescription:
          'Every lead has been reviewed. New ones appear here as they arrive.',
      }
    : {
        emptyTitle: 'Nothing reviewed yet.',
        emptyDescription:
          'Decisions and archived leads land here as they are made.',
      }
}

const LEADS_PRELOAD_MARGIN = 3
const FADE_OUT_MS = 300
const FADE_OUT_CLASSES =
  'motion-safe:animate-out motion-safe:fade-out motion-safe:duration-300 fill-mode-forwards'

// Null at the ends: the inbox is paged, so wrapping would claim an order the reviewer can't see.
function adjacentLeads(
  leads: DealLeadListItem[],
  selectedLeadId: string | null
): { previousLeadId: string | null; nextLeadId: string | null } {
  const at = leads.findIndex((lead) => lead.id === selectedLeadId)
  if (at === -1) return { previousLeadId: null, nextLeadId: null }

  return {
    previousLeadId: at > 0 ? leads[at - 1].id : null,
    nextLeadId: at < leads.length - 1 ? leads[at + 1].id : null,
  }
}

export function DealLeadInbox() {
  const [view, setView] = useState<DealLeadView>('inbox')
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null)
  const [filterState, setFilterState] =
    useState<DealLeadFilterState>(EMPTY_LEAD_FILTERS)
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set())
  const [sorting, setSorting] = useState<SortingState>([])

  const selection: LeadSelection = useMemo(
    () => ({
      ids: selectedIds,
      toggle: (id) =>
        setSelectedIds((current) => {
          const next = new Set(current)
          if (next.has(id)) next.delete(id)
          else next.add(id)
          return next
        }),
      clear: () => setSelectedIds(new Set()),
      selectAll: (ids) => setSelectedIds(new Set(ids)),
    }),
    [selectedIds]
  )

  // Leads decided this session, held in the Inbox where they were decided; cleared on tab switch.
  const [sessionDecidedRows, setSessionDecidedRows] = useState<
    ReadonlyMap<string, HeldLead>
  >(() => new Map())
  const shownLeads = useRef<DealLeadListItem[]>([])

  const noteDecided = useCallback((detail: DealLeadDetail) => {
    setSessionDecidedRows((current) => {
      const index = shownLeads.current.findIndex(
        (lead) => lead.id === detail.id
      )
      const place = current.get(detail.id) ?? {
        index,
        after: index > 0 ? shownLeads.current[index - 1].id : null,
      }
      return new Map(current).set(detail.id, { ...place, lead: detail })
    })
  }, [])

  const noteSent = useCallback((detail: DealLeadDetail) => {
    if (detail.dealCloudSyncState !== 'SENT') return
    setSessionDecidedRows((current) => {
      if (!current.has(detail.id)) return current
      const next = new Map(current)
      next.delete(detail.id)
      return next
    })
  }, [])

  const search = useDebouncedValue(filterState.search)
  const filters = useMemo(
    () => filtersForView(view, { ...filterState, search }),
    [view, filterState, search]
  )
  const sorts = useMemo(() => sortsForView(sorting), [sorting])

  const send = useUndoableSend()
  const counts = useDealLeadCounts()
  const {
    leads,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = useDealLeadsSearch({
    view,
    filters,
    sorts,
    live: (counts.data?.processing ?? 0) > 0,
  })

  // Held rows read the detail cache: a decision-time snapshot goes stale on the next write.
  const heldIds = useMemo(
    () => Array.from(sessionDecidedRows.keys()),
    [sessionDecidedRows]
  )
  const heldDetails = useCachedDealLeads(heldIds)
  const heldRows = useMemo(
    () =>
      new Map(
        Array.from(sessionDecidedRows, ([id, entry], index) => [
          id,
          { ...entry, lead: heldDetails[index] ?? entry.lead },
        ])
      ),
    [sessionDecidedRows, heldDetails]
  )

  // Sent rows fade out instead of jumping; compared only within one query (same filters and sorts).
  const [leaving, setLeaving] = useState<ReadonlyMap<string, HeldLead>>(
    () => new Map()
  )
  const lastPage = useRef({ filters, sorts, leads })
  useEffect(() => {
    const before = lastPage.current
    lastPage.current = { filters, sorts, leads }
    if (
      view !== 'inbox' ||
      before.filters !== filters ||
      before.sorts !== sorts
    ) {
      return
    }
    const gone = leftWhileSending(before.leads, leads)
    if (gone.size > 0) setLeaving(gone)
  }, [view, filters, sorts, leads])
  useEffect(() => {
    if (leaving.size === 0) return
    const timer = setTimeout(() => setLeaving(new Map()), FADE_OUT_MS)
    return () => clearTimeout(timer)
  }, [leaving])

  const tableLeads = useMemo(
    () =>
      view === 'inbox'
        ? keepInPlace(leads, new Map([...heldRows, ...leaving]))
        : leads,
    [view, leads, heldRows, leaving]
  )
  useEffect(() => {
    shownLeads.current = tableLeads
  }, [tableLeads])

  // Prefetch ahead of the panel's cursor so arrowing never dead-ends at a page boundary.
  useEffect(() => {
    if (!selectedLeadId || !hasNextPage || isFetchingNextPage) return

    const at = tableLeads.findIndex((lead) => lead.id === selectedLeadId)
    if (at === -1 || at < tableLeads.length - 1 - LEADS_PRELOAD_MARGIN) return

    void fetchNextPage()
  }, [
    selectedLeadId,
    tableLeads,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ])

  // After the query: the select-all header needs the rows it governs.
  const nothingToFilter =
    !isLoading &&
    !isError &&
    tableLeads.length === 0 &&
    !hasActiveLeadFilters(filterState)

  const columns = useDealLeadColumns(view)
  const tableState: InboxTableState = useMemo(
    () => ({
      selection,
      leads: tableLeads,
      onSelect: setSelectedLeadId,
      onDecided: noteDecided,
      onSent: noteSent,
    }),
    [selection, tableLeads, noteDecided, noteSent]
  )

  return (
    <div className="flex h-full flex-col gap-4">
      <PageHeader title="Deal Review" useSeparator={false}>
        <div className="flex items-center gap-2">
          <DealCloudSendStatus />
          <IntakePanel onSelectLead={setSelectedLeadId} />
        </div>
      </PageHeader>

      <Tabs
        value={view}
        onValueChange={(next) => {
          setSelectedIds(new Set())

          setSessionDecidedRows(new Map())

          // Reset leaving History: Inbox has no control for it but the toolbar still counts it active.
          if (next !== 'history') {
            setFilterState((current) => ({ ...current, review: 'all' }))
          }

          setView(next as DealLeadView)
        }}
        // `relative` anchors the floating selection bar.
        className="relative flex min-h-0 flex-1 flex-col gap-4 pr-4"
      >
        <div className="border-b border-primary/10 pb-4">
          <TabsList>
            <CountTab
              view="inbox"
              count={counts.data?.inbox}
              isLoading={counts.isLoading}
            />
            <CountTab
              view="history"
              count={counts.data?.history}
              isLoading={counts.isLoading}
            />
          </TabsList>
        </div>

        {!nothingToFilter && (
          <InboxToolbar
            state={filterState}
            onChange={setFilterState}
            leads={tableLeads}
            leading={
              view === 'history' ? (
                <DecisionFilter
                  value={filterState.review}
                  onChange={(review) =>
                    setFilterState({ ...filterState, review })
                  }
                />
              ) : null
            }
          />
        )}

        {/* Real panels: a bare TabsList's aria-controls point at nothing (a11y critical violation). */}
        {DEAL_LEAD_VIEWS.map((tabView) => (
          <TabsContent
            key={tabView}
            value={tabView}
            className="flex min-h-0 flex-1 flex-col"
          >
            <InboxTableContext.Provider value={tableState}>
              <DataTable
                data={tableLeads}
                columns={columns}
                isLoading={isLoading}
                isError={isError}
                isLoadingMore={isFetchingNextPage}
                hasMore={hasNextPage}
                onLoadMore={() => void fetchNextPage()}
                onRefresh={() => void refetch()}
                enableColumnReorder={false}
                enableColumnResize={false}
                sorting={sorting}
                onSortChange={setSorting}
                onRowClick={(lead) => setSelectedLeadId(lead.id)}
                isRowClickable={(lead) => !isInProgress(lead)}
                spanFrom={(lead) =>
                  isInProgress(lead)
                    ? {
                        columnId: 'companyName',
                        content: <RowProgress lead={lead} />,
                      }
                    : null
                }
                rowClassName={(lead) =>
                  cn(
                    'group/row',
                    leaving.has(lead.id) && FADE_OUT_CLASSES,
                    isInProgress(lead) &&
                      'opacity-60 [&_button]:pointer-events-none [&_input]:pointer-events-none'
                  )
                }
                enableRowClick
                className={inboxTableClasses(tabView)}
                {...emptyStateFor(tabView, filterState, () =>
                  setFilterState(EMPTY_LEAD_FILTERS)
                )}
              />
            </InboxTableContext.Provider>
          </TabsContent>
        ))}
        {/* Last in the tree, so it paints over the rows. */}
        {view !== 'history' && (
          <SelectionBar selection={selection} leads={tableLeads} send={send} />
        )}
      </Tabs>

      <DealLeadDetailSheet
        selectedLeadId={selectedLeadId}
        onClose={() => setSelectedLeadId(null)}
        onDecided={noteDecided}
        onSent={noteSent}
        {...adjacentLeads(tableLeads, selectedLeadId)}
        onNavigate={setSelectedLeadId}
      />
    </div>
  )
}
